import {useMemo, useState} from "react";
import {Modal} from "rsuite";
import {SystemEventDto} from "../../../api";
import {formatDateTime, formatDayTitle, formatTime, groupByDay, toDate} from "./journal.utils";

export type LaneTone = { tone: string; badge: string; period: string | null };

type LaneHistoryProps = {
  name: string;
  events: SystemEventDto[];
  // Цвет, подпись бейджа и длительность — те же, что в общем списке
  describe: (event: SystemEventDto) => LaneTone;
  onClose: () => void;
  // Показать события этого устройства в общем списке журнала
  onShowInList: () => void;
  // Перейти к механизму в разделе «Устройства BIGLine»
  onOpenDevice?: () => void;
};

type Kind = { key: string; label: string; test: (event: SystemEventDto) => boolean };

const KINDS: Kind[] = [
  {key: "all", label: "Все", test: () => true},
  {key: "faults", label: "Сбои", test: (x) => x.kind === "MechanismFault" || x.severity === "Fault"},
  {key: "connection", label: "Связь", test: (x) => x.kind === "Connection"},
  {key: "power", label: "Питание", test: (x) => x.kind === "Power"},
  {key: "door", label: "Дверь и удары", test: (x) => x.kind === "Door" || x.kind === "Shock"},
  {key: "restarts", label: "Перезапуски", test: (x) => x.kind === "Restart"},
  {key: "actions", label: "Действия людей", test: (x) => x.kind === "UserAction"},
];

const isFault = (event: SystemEventDto) => event.kind === "MechanismFault" || event.severity === "Fault";

const faultsWord = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return "сбой";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "сбоя";
  return "сбоев";
};

// Окно «История» одной дорожки таймлайна: все события механизма (диммера, станции) за выбранный период
const LaneHistory = ({name, events, describe, onClose, onShowInList, onOpenDevice}: LaneHistoryProps) => {
  const sorted = useMemo(
    () => [...events].sort((a, b) => (toDate(b.time)?.getTime() ?? 0) - (toDate(a.time)?.getTime() ?? 0)),
    [events],
  );
  const kinds = useMemo(() => KINDS.filter((kind) => kind.key === "all" || sorted.some(kind.test)), [sorted]);
  const faults = useMemo(() => sorted.filter(isFault), [sorted]);
  const [kind, setKind] = useState(() => (faults.length > 0 ? "faults" : "all"));

  const list = useMemo(() => {
    const test = KINDS.find((x) => x.key === kind)?.test ?? (() => true);
    return sorted.filter(test);
  }, [sorted, kind]);
  const groups = useMemo(() => groupByDay(list, (x) => x.time), [list]);

  return (
    <Modal open onClose={onClose} size="md" className="lane-history">
      <Modal.Header>
        <Modal.Title>{name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="lane-history__summary">
          {faults.length > 0
            ? <>За период: <b>{faults.length} {faultsWord(faults.length)}</b> · последний {formatDateTime(faults[0].time)}</>
            : <>За период сбоев нет</>}
          <span className="lane-history__total">всего событий: {sorted.length}</span>
        </div>

        {kinds.length > 1 && (
          <div className="journal-chips lane-history__chips">
            {kinds.map((item) => (
              <button key={item.key} type="button"
                      className={`journal-chip${kind === item.key ? " journal-chip--active" : ""}`}
                      onClick={() => setKind(item.key)}>
                {item.label} <span className="lane-history__count">{item.key === "all" ? sorted.length : sorted.filter(item.test).length}</span>
              </button>
            ))}
          </div>
        )}

        {list.length === 0 && <div className="journal__state">Событий нет</div>}

        <div className="lane-history__list">
          {groups.map((group) => (
            <div key={group.key} className="journal-events__group">
              <div className="journal-table__day">{formatDayTitle(group.key)}</div>
              {group.items.map((event, index) => {
                const look = describe(event);
                return (
                  <div key={`${event.time}-${index}`} className="journal-event lane-history__event">
                    <span className="journal-event__time">{formatTime(event.time)}</span>
                    <span className={`journal-badge journal-badge--${look.tone}`}>{look.badge}</span>
                    <span className="journal-event__text">
                      {event.text}
                      {[event.details, look.period].filter(Boolean).length > 0 && (
                        <small>{[event.details, look.period].filter(Boolean).join(" · ")}</small>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="lane-history__actions">
          {onOpenDevice && (
            <button type="button" className="journal-chip" onClick={onOpenDevice}>Открыть механизм</button>
          )}
          <button type="button" className="journal-chip" onClick={onShowInList}>Показать в общем списке</button>
          <button type="button" className="journal-chip journal-chip--active" onClick={onClose}>Закрыть</button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default LaneHistory;
